import { Button } from '@/components/ui/button';

export interface RetryButtonProps {
  /** Runs the read again. */
  onRetry: () => void;
  /**
   * Whether a read is in flight right now.
   *
   * Optional only because a handful of callers hold a hook that does not expose
   * it. Pass it wherever it is available: it is what turns the button from a
   * thing you can press four times into a thing that tells you it is working.
   */
  isFetching?: boolean;
}

/**
 * The one control offered beside a failed read.
 *
 * There were two, and the difference was invisible in review and obvious in
 * use. Four surfaces — the console's three pages and the usage page — disabled
 * the button while the retry was in flight and said "Trying…". Ten feature
 * surfaces shipped a plain, always-enabled "Try again", so pressing it looked
 * like nothing had happened and pressing it four times queued four reads.
 *
 * The busy state is the whole point. A retry answers in the same amount of time
 * the failed read took, which is long enough for a reader to conclude the button
 * is broken.
 */
export function RetryButton({ onRetry, isFetching = false }: RetryButtonProps) {
  return (
    <Button variant="outline" size="sm" onClick={onRetry} disabled={isFetching}>
      {isFetching ? 'Trying…' : 'Try again'}
    </Button>
  );
}
